import { IconButton } from "@mui/material";
import React, { Fragment, useContext, useState } from "react";
import { FiEdit, FiShoppingBag, FiTrash } from "react-icons/fi";
import propTypes from "prop-types";

import { api, UpsellPackage } from "../../../../logic/api";

import flex from "../../../../styles/flex";

import { PackageAvailability } from "../../../packages/builder/PackageBuilder";

import ErrorDialog from "../../../notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../notifications/progressDialog/progressDialog";
import ConfirmationDialog from "../../../notifications/confirmationDialog/ConfirmationDialog";

import { SessionContext } from "../../../../utility/useSession";
import { isAdmin } from "../../../../utility/utils";
import { useDate } from "../../../../utility/useDate";
import { useTranslation } from "react-i18next";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";

const UpsellPackageListItem: React.FunctionComponent<{
  upsellPackage: UpsellPackage,
  onEditPackage: () => void,
}> = (props) => {
  const [pkg] = useState(props.upsellPackage);
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const [showDeletePackage, setShowDeletePackage] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const { toFormattedDateString, localizeDate } = useDate();
  const requestDeletePackage = api.hooks.upsell.delete();
  const { hasPermission } = useUserPermissions();
  const isRbacReleased = useReleaseManagement(ReleaseFlag.releaseRbac);


  const deletePackage = () => {
    if (pkg.id != null) {
      requestDeletePackage.send(session.current.context, {
        id: pkg.id
      }, (result) => {
        if (result != null) {
          setDeleted(true);
        }
      });
    }
  }

  if (deleted || session.current.site == null) {
    return <Fragment />
  }

  let availability: PackageAvailability = PackageAvailability.available;
  let iconColour = "black";

  const localizedStartOfDay = localizeDate(new Date())
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const localizedStartOfPackageStartDate = localizeDate(new Date(pkg.startDate))
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  const packageStartsInTheFuture = Boolean(
    localizedStartOfPackageStartDate.diff(localizedStartOfDay).milliseconds > 0
  );
  if (packageStartsInTheFuture) {
    availability = PackageAvailability.NotYetAvailable;
    iconColour = "orange";
  }

  if (pkg.expires && pkg.endDate != null) {

    const localizedStartOfPackageEndDate = localizeDate(new Date(pkg.endDate))
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    const packagedHasExpired = Boolean(
      localizedStartOfPackageEndDate.diff(localizedStartOfDay).milliseconds < 0
    );

    if (packagedHasExpired) {
      iconColour = "red";
      availability = PackageAvailability.NoLongerAvailable;
    }
  }

  const getAvailabilityComponent = () => {
    const startDate = toFormattedDateString(new Date(pkg.startDate));
    const endDate = pkg.endDate ? toFormattedDateString(new Date(pkg.endDate)) : '';
    let availabilityText = '';

    if (availability === PackageAvailability.NotYetAvailable) {
      availabilityText = `[${t("packages.notYetAvailable")}]`;
    } else if (availability === PackageAvailability.NoLongerAvailable) {
      availabilityText = `[${t("packages.noLongerAvailable")}]`;;
    }

    const textColor = availability === PackageAvailability.NotYetAvailable ? "orange" : "red";

    return (<small>
      <b>{startDate}</b> {endDate ? (<b>- {endDate}</b>) : <></>}
      {(availabilityText ? <span style={{ color: textColor }}><b> {availabilityText} </b></span> : <></>)}
    </small>);
  }

  return (
    <Fragment>
      <ErrorDialog err={requestDeletePackage.err}></ErrorDialog>
      <ProgressDialog status={requestDeletePackage.status}></ProgressDialog>

      <ConfirmationDialog
        title={t("upsells.confirmDelete")}
        message={t("upsells.upsellConfirmDelete", { name: pkg.name })}
        show={showDeletePackage}
        onClose={(confirm) => {
          setShowDeletePackage(false);
          if (confirm == true) {
            deletePackage();
          }
        }}
      ></ConfirmationDialog>

      <div>
        <div style={{ ...flex.flexRows, justifyContent: "center", alignItems: "center" }}>

          <div style={flex.flexAuto}>
            <div>
              <IconButton disabled={true}>
                <FiShoppingBag color={iconColour}></FiShoppingBag>
              </IconButton>
            </div>
          </div>

          <div style={{ ...flex.flexFill, textAlign: "left" }}>
            <div>
              <b>{pkg.name}</b>
            </div>

            {(availability != PackageAvailability.available) && (
              <div>
                {getAvailabilityComponent()}
              </div>
            )}

            <div>
              <small style={{ color: "rgba(0, 0, 0, 0.54)" }}>
                <b>{pkg.code}</b>
                {(pkg.ref != null && pkg.ref.length > 0) && (
                  <span> / <b>{pkg.ref}</b></span>
                )}
              </small>
            </div>
          </div>

          <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
            {(isRbacReleased ? hasPermission("bookingManagement.upsells.update") : isAdmin(session)) && (
              <div>
                <IconButton
                  onClick={() => {
                    props.onEditPackage();
                  }}
                >
                  <FiEdit></FiEdit>
                </IconButton>
                <div><small>{t("general.edit")}</small></div>
              </div>
            )}
          </div>

          {(isRbacReleased ? hasPermission("bookingManagement.upsells.delete") : isAdmin(session)) && (
            <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
              <IconButton
                onClick={() => {
                  setShowDeletePackage(true);
                }}
              >
                <FiTrash></FiTrash>
              </IconButton>
              <div><small>{t("general.delete")}</small></div>
            </div>
          )}

          <div style={flex.flexAuto}>
            <div>
              <IconButton disabled={true}>
                <FiShoppingBag color={iconColour}></FiShoppingBag>
              </IconButton>
            </div>
          </div>

          <div style={{ ...flex.flexFill, textAlign: "left" }}>
            <div>
              <b>{pkg.name}</b>
            </div>

            <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
              {(isRbacReleased ? hasPermission("bookingManagement.upsells.update") : isAdmin(session)) && (
                <div>
                  <IconButton
                    onClick={() => {
                      props.onEditPackage();
                    }}
                  >
                    <FiEdit></FiEdit>
                  </IconButton>
                  <div><small>{t("general.edit")}</small></div>
                </div>
              )}
            </div>

            {(isRbacReleased ? hasPermission("bookingManagement.upsells.delete") : isAdmin(session)) && (
              <div style={{ ...flex.flexAuto, textAlign: "center", minWidth: 80 }}>
                <IconButton
                  onClick={() => {
                    setShowDeletePackage(true);
                  }}
                >
                  <FiTrash></FiTrash>
                </IconButton>
                <div><small>{t("general.delete")}</small></div>
              </div>
            )}
          </div>
        </div >
      </div>
    </Fragment>
  );
}

UpsellPackageListItem.propTypes = {
  upsellPackage: propTypes.any.isRequired,
  onEditPackage: propTypes.func.isRequired
}

export default UpsellPackageListItem;
